<script lang="ts">
	import Modal from '$lib/modal/v2/modal.svelte';
	import SbRichtext from '../components/sbRichtext.svelte';
	import PaymentMethodMessagingElement from '$lib/stripe/paymentMethodMessagingElement.svelte';
	import ProductPrice from '../parts/productPrice.svelte';
	import { onDestroy } from 'svelte';
	import type { Stripe } from 'stripe';
	import log from '$lib/utils/log';
	import type { StripeProductData } from '../types';
	import { editable } from '../editable';
	import Blok from '../blok.svelte';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { ReactiveProduct } from '$lib/state/reactiveProduct.svelte';
	import AffirmModalContent from '$lib/modals/bnplModal/affirmModalContent.svelte';

	interface Props {
		content: ISbStoryData<{
			title: string;
			subtitle: ISbRichtext;
			stripe_product: StripeProductData;
			body: ISbStoryData[];
			body_after: ISbStoryData[];
			special_promo_code?: string;
		}>['content'];
	}

	let { content }: Props = $props();

	let data = $derived(
		new ReactiveProduct(content.stripe_product.priceLookupKey, content.stripe_product.data)
	);

	onDestroy(() => {
		data.unsubscribe();
	});

	let specialPromoCode = $state<Stripe.PromotionCode>();

	async function fetchSpecialPromo(couponCode: string) {
		try {
			const response = await fetch('/api/stripe/promotion-codes?couponCode=' + couponCode);

			specialPromoCode = await response.json();
		} catch (e) {
			log.exception(e);

			specialPromoCode = undefined;
		}
	}

	$effect(() => {
		if (!data.promotionCode && content.special_promo_code) {
			fetchSpecialPromo(content.special_promo_code);
		} else {
			specialPromoCode = undefined;
		}
	});

	let promoCode = $derived(data.promotionCode || specialPromoCode);
</script>

<div
	use:editable={content}
	class="col-span-full flex flex-col justify-center max-lg:mx-8 lg:col-start-12 lg:col-end-[17]"
>
	{#each content.body || [] as block}
		<Blok content={block} />
	{/each}

	<h1 class="text-[32px]/[100%] font-semibold text-brown-1 lg:text-[48px]" itemprop="name">
		{content.title}
	</h1>

	<div
		class="mt-4 flex min-h-[74px] flex-col gap-2 text-[24px]/[100%] lg:mt-3 lg:min-h-[84px] lg:text-[32px]"
	>
		<ProductPrice
			{promoCode}
			price={data.price.unit_amount || 99900}
			isSpecialPromo={!!specialPromoCode}
		/>
		<PaymentMethodMessagingElement
			amount={data.price.unit_amount || 99900}
			coupon={promoCode?.coupon}
			id="payment-method-messaging-element-pdp-hero"
		/>
	</div>

	<div
		class="mt-3 text-balance text-[16px]/[140%] tracking-[-.16px] text-brown-3 lg:mt-4"
		itemprop="description"
	>
		<SbRichtext text={content.subtitle} />
	</div>

	{#each content.body_after || [] as block}
		<Blok content={block} />
	{/each}

	<div class="lg:h-19"></div>
</div>

<Modal withDesktopPadding size="regular" modalName="Affirm Modal">
	<AffirmModalContent />
</Modal>
